import React from 'react';
import classNames from 'classnames';

import type { DeviceInfo } from 'js/src/components/app/recorder/types';
import { DeviceTypes } from 'js/src/components/app/recorder/types';
import { BUTTON_COLORS, BUTTON_WIDTHS } from 'js/src/components/common/ui/Button';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown, DROPDOWN_DIVIDER, DROPDOWN_HEADER } from 'js/src/components/common/ui/Dropdown';

const toggleProps = {
    iconLeft: 'settings',
    className: 'device-settings-toggle d-inline-block',
    width: BUTTON_WIDTHS.NO_MIN_WIDTH,
    color: BUTTON_COLORS.SIMPLE,
};

const convertDeviceInputsToDropdownItems = (
    deviceType: DeviceTypes,
    deviceInputs: DeviceInfo[],
    activeDeviceId?: string,
    disabled = false,
): PlainDropdownItem<SelectDataType>[] =>
    deviceInputs.map((deviceInput) => ({
        id: deviceInput.id,
        text: deviceInput.label,
        className: classNames({
            'fw-bold': deviceInput.id === activeDeviceId,
        }),
        onSelectData: {
            deviceType,
            deviceId: deviceInput.id,
        },
        disabled,
    }));

type SelectDataType = {
    deviceType: DeviceTypes;
    deviceId: string;
};

type Props = {
    availableMicrophoneDevices: DeviceInfo[];
    availableCameraDevices: DeviceInfo[];
    availableScreenDevices: DeviceInfo[];
    activeMicrophoneDeviceId: string | undefined;
    activeCameraDeviceId: string | undefined;
    activeScreenDeviceId: string | undefined;
    onDeviceChange: (deviceType: DeviceTypes, deviceId: string) => void;
};

const DeviceSelectorButton: React.FC<Props> = ({
    availableMicrophoneDevices,
    availableCameraDevices,
    availableScreenDevices,
    activeMicrophoneDeviceId,
    activeCameraDeviceId,
    activeScreenDeviceId,
    onDeviceChange,
}) => {
    const onSelect = React.useCallback(
        (selectedOption: SelectDataType) => {
            onDeviceChange(selectedOption.deviceType, selectedOption.deviceId);
        },
        [onDeviceChange],
    );

    const dropdownItems = React.useMemo(() => {
        const items: PlainDropdownItem<SelectDataType>[] = [];

        if (availableMicrophoneDevices.length > 0) {
            items.push({
                id: DROPDOWN_HEADER,
                text: 'Mic',
                icon: 'mic',
                className: 'd-flex align-items-center',
            });
            const audioDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.AUDIO,
                availableMicrophoneDevices,
                activeMicrophoneDeviceId,
            );
            items.push(...audioDeviceItems);
            items.push({ id: DROPDOWN_DIVIDER });
        }

        if (availableCameraDevices.length > 0) {
            items.push({
                id: DROPDOWN_HEADER,
                text: 'Camera',
                icon: 'videocam',
                className: 'd-flex align-items-center',
            });
            const videoDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.VIDEO,
                availableCameraDevices,
                activeCameraDeviceId,
            );
            items.push(...videoDeviceItems);
            items.push({ id: DROPDOWN_DIVIDER });
        }

        if (availableScreenDevices.length > 0) {
            items.push({
                id: DROPDOWN_HEADER,
                text: 'Screen recording',
                icon: 'screen_record',
                className: 'd-flex align-items-center',
            });
            const screenDeviceItems = convertDeviceInputsToDropdownItems(
                DeviceTypes.SCREEN,
                availableScreenDevices,
                activeScreenDeviceId,
            );
            items.push(...screenDeviceItems);
        }

        return items;
    }, [
        activeCameraDeviceId,
        activeMicrophoneDeviceId,
        activeScreenDeviceId,
        availableCameraDevices,
        availableMicrophoneDevices,
        availableScreenDevices,
    ]);

    return (
        <Dropdown
            toggleProps={toggleProps}
            items={dropdownItems}
            onItemSelect={onSelect}
            breakOverflow
        />
    );
};
export default React.memo(DeviceSelectorButton);
